import { useQuery } from "@apollo/client";
import { ExceptionReason } from "@api/graphql/generated/generated-types";
import { useAuth } from "@src/common/hooks/useAuth";
import { getUserFacingErrorMessage } from "@src/common/lib/NetworkErrorHandling";
import { GET_EXCEPTION_REASONS } from "../contexts/ExceptionReasonsCoreDataProvider";

// A picker needs the whole list, not a page of it. cube's ExceptionReasonsQuery
// applies no cap to `limit`, so one request is enough and no paging is needed.
const OPTIONS_LIMIT = 500;

interface UseExceptionReasonOptionsArgs {
  skip?: boolean;
}

interface UseExceptionReasonOptionsResult {
  exceptionReasons: ExceptionReason[];
  isLoading: boolean;
  hasError: boolean;
  // The app's standard translated copy for this failure, when it has one — so an
  // authorization or BAD_REQUEST failure reads the same here as it would in a
  // toast, rather than as generic "could not load" text. undefined for a
  // network-only failure, where the caller's own retry copy is the better
  // message. Reported inline rather than as a toast because this hook feeds a
  // modal that renders the failure in place; toasting as well would report one
  // failure twice.
  errorMessage?: string;
}

export function useExceptionReasonOptions({
  skip = false,
}: UseExceptionReasonOptionsArgs = {}): UseExceptionReasonOptionsResult {
  const { courierId } = useAuth();

  const { data, loading, error } = useQuery(GET_EXCEPTION_REASONS, {
    variables: { courierId: courierId ?? "", after: 0, limit: OPTIONS_LIMIT },
    skip: skip || !courierId,
    fetchPolicy: "network-only",
  });

  const edges = data?.exceptionReasonsPaginated?.edges ?? [];
  const exceptionReasons: ExceptionReason[] = edges
    .map((edge: { node?: ExceptionReason } | null) => edge?.node)
    .filter(
      (node: ExceptionReason | undefined): node is ExceptionReason => !!node
    );

  return {
    exceptionReasons,
    isLoading: loading,
    hasError: !!error,
    errorMessage: getUserFacingErrorMessage(error),
  };
}
